import React, { useLayoutEffect, useEffect } from 'react';
import HeaderV3 from '../../../components/HeaderV3/headerV3.component';
import FooterSection from '../../../components/FooterSection/footersection.component';
import OrbixContact from '../../../components/ContactSection/contact.component';
import HeroSection from './aiquality-components/HeroSection/HeroSection';
import HowItWorks from './aiquality-components/HowItWorks/HowItWorks';
import SeniorAuditor from './aiquality-components/SeniorAuditor/SeniorAuditor';
import ContinuousReview from './aiquality-components/ContinuousReview/ContinuousReview';
import Monitoring from './aiquality-components/Monitoring/Monitoring';
import AnalyticsReporting from './aiquality-components/AnalyticsReporting/AnalyticsReporting';
import Delivery from './aiquality-components/Delivery/Delivery';
import './aiquality.component.css';

function AIQualityProgram() {
  useLayoutEffect(() => {
    document.body.style.backgroundColor = '#131022';
    return () => { document.body.style.backgroundColor = ''; };
  }, []);

  // Scroll reveals — mirrors the design's per-group stagger.
  useEffect(() => {
    const reveals = document.querySelectorAll('#aqp-page .aqp-reveal');
    const obs = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('aqp-visible');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    reveals.forEach(el => {
      const siblings = el.parentElement
        ? Array.from(el.parentElement.querySelectorAll(':scope > .aqp-reveal'))
        : [];
      const idx = siblings.indexOf(el);
      if (idx > 0) el.style.transitionDelay = (idx * 95) + 'ms';
      obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  // Fit the fixed-width animated diagrams into the available column width.
  useEffect(() => {
    const root = document.getElementById('aqp-page');
    if (!root) return;

    const fitAll = () => {
      const mobile = window.innerWidth <= 700;
      root.querySelectorAll('[data-fit]').forEach(wrap => {
        const scale = wrap.querySelector('[data-fit-scale]');
        if (!scale) return;
        const natW = parseFloat(wrap.getAttribute('data-fit')) || 1200;
        const avail = wrap.clientWidth;
        if (avail < 1) return;
        if (mobile) {
          // On phones, scale the diagram down to a legible floor and let the
          // section scroll sideways for the remaining overflow.
          const sc = Math.max(Math.min(1, avail / natW), 0.5);
          scale.style.transform = 'none';
          scale.style.zoom = String(sc);
          wrap.style.overflowX = 'auto';
          wrap.style.overflowY = 'hidden';
          wrap.style.justifyContent = 'flex-start';
          wrap.style.height = 'auto';
          wrap.classList.toggle('aqp-fit--scrollable', natW * sc > avail + 1);
        } else {
          scale.style.zoom = '';
          scale.style.transform = 'scale(' + Math.min(1, avail / natW) + ')';
          wrap.style.overflowX = '';
          wrap.style.overflowY = '';
          wrap.style.justifyContent = '';
          wrap.classList.remove('aqp-fit--scrollable');
          const natH = Math.max(scale.scrollHeight, scale.offsetHeight) || 0;
          if (natH > 0) wrap.style.height = (natH * Math.min(1, avail / natW)) + 'px';
        }
      });
    };

    window.addEventListener('resize', fitAll);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(fitAll);
      root.querySelectorAll('[data-fit] [data-fit-scale]').forEach(el => ro.observe(el));
      root.querySelectorAll('[data-fit]').forEach(el => ro.observe(el));
    }
    fitAll();
    const timers = [200, 600, 1200, 2400].map(t => setTimeout(fitAll, t));
    return () => {
      window.removeEventListener('resize', fitAll);
      if (ro) ro.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  return (
    <main className="page-wrapper dark-mode">
      <HeaderV3 />
      <div id="aqp-page">
        <HeroSection />
        <HowItWorks />
        <SeniorAuditor />
        <ContinuousReview />
        <Monitoring />
        <AnalyticsReporting />
        <Delivery />
      </div>
      <OrbixContact
        subheading="Contact us today."
        heading="Reach out to our team to book a demo or learn more."
      />
      <FooterSection />
    </main>
  );
}

export default AIQualityProgram;
